<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Brief text label that appears when the pointer rests on a target element.
 *
 * NAMES AN ICON, AND NOTHING ELSE. A tooltip is out of reach on touch and invisible until the reader
 * thinks to point at something, so it must never carry information they need. One sentence they need
 * to decide is supporting text under the control's label (a field's `hint`, a setting row's
 * `description`); anything longer or occasional goes behind an HrIconHintBtn.
 *
 * THE ICON BUTTON STILL NEEDS ITS OWN `aria-label`. A tooltip does not open on keyboard focus and is
 * not wired to its target, so it shows the name to mouse users only; screen readers and keyboard
 * users get the name from the button itself.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen Naming an icon-only control
 * @avoidWhen The text is information the reader needs: use supporting text, or HrIconHintBtn when it is longer
 * @related HrIconHintBtn
 * @a11y Not exposed as the target's accessible name: give the icon button an `aria-label` too.
 * @see https://quasar.dev/vue-components/tooltip Quasar q-tooltip API
 */
defineOptions({ name: 'HrTooltip' });

const attrs = useAttrs();
const { hasTheme } = useTheme();

const tooltipClass = computed(() => [
  hasTheme.value && 'hr-tooltip',
  attrs.class,
].filter(Boolean));
</script>

<template>
  <q-tooltip
    v-bind="attrs"
    :class="tooltipClass"
  >
    <!-- @slot Tooltip text or content. -->
    <slot />
  </q-tooltip>
</template>
